<template>
  <v-container
    class="h-100 d-flex flex-column pa-0"
    fluid
    style="gap: 1.5rem"
  >
    <v-row no-gutters class="flex-grow-0">
      <v-col cols="12">
        <v-row dense>
          <v-col cols="12" md="8">
            <v-text-field
              autofocus
              outlined
              dense
              clearable
              hide-details
              :value="pagination.search"
              @input="debounceSearch($event)"
              @click:clear="clearSearch()"
              flat
              prepend-inner-icon="mdi-magnify"
              label="Buscar"
              color="primary"
            />
          </v-col>

          <v-col>
            <v-select
              dense
              label="Tipo"
              outlined
              :value="pagination.type"
              @input="debounceType($event)"
              :items="typeOptions"
              item-text="name"
              item-value="value"
              hide-details
              clearable
              flat
              multiple
            />
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <v-row no-gutters v-if="visitLoading" class="loader-container flex-grow-1">
      <Loader text="Cargando agenda" />
    </v-row>

    <v-row no-gutters class="h-100 flex-grow-1" v-else>
      <!-- VISTA MOBILE -->
      <v-data-table
        class="hidden-md-and-up"
        :headers="computedHeaders"
        :items-per-page.sync="pagination.itemsPerPage"
        :server-items-length="totalVisits"
        :page.sync="pagination.page"
        :items="visits"
        hide-default-header
        style="width: 100%"
        :loading="visitLoading"
        loading-text="Cargando listado..."
        no-data-text="No hay visitas agendadas"
        no-results-text="No se encontraron resultados"
        :footer-props="{
          itemsPerPageOptions: pagination.rowsPerPageOptions,
          itemsPerPageText: 'Visitas por página',
        }"
      >
        <template v-slot:item="{ item: visit }">
          <div>
            <h3 align="center" class="date">
              {{ formatDate(visit.visit_date) }}
            </h3>
          </div>

          <VisitItem
            :showDate="
              pagination.search ||
              ($store.state.visit_type && $store.state.visit_type + '')
            "
            :visit="visit"
            @show-request-detail="showRequestDetail"
            :is-navigating="isNavigating"
          />
        </template>
      </v-data-table>
      <!-- FIN VISTA MOBILE -->

      <!-- VISTA DESKTOP -->
      <v-data-table
        class="hidden-sm-and-down"
        :headers="computedHeaders"
        :items-per-page.sync="pagination.itemsPerPage"
        :server-items-length="totalVisits"
        :page.sync="pagination.page"
        hide-default-header
        :items="visits"
        style="width: 100%"
        :loading="visitLoading"
        loading-text="Cargando listado..."
        no-data-text="No hay visitas agendadas"
        no-results-text="No se encontraron resultados"
        group-by="visit_date"
        show-group-by
        :footer-props="{
          itemsPerPageOptions: pagination.rowsPerPageOptions,
          itemsPerPageText: 'Visitas por página',
          groupBy: 'visit_date',
        }"
      >
        <template v-slot:header="{ props: { headers }, on }">
          <tr>
            <th
              v-for="header in headers"
              :class="header.class"
              :width="header.width"
              :key="header.text"
            >
              <div
                style="
                  display: flex;
                  justify-content: start;
                  align-items: center;
                  gap: 0.5rem;
                "
              >
                <span class="text-subtitle-1 font-weight-bold text-uppercase">{{
                  header.text
                }}</span>
                <v-icon
                  v-if="header.groupable"
                  title="agrupar por fecha"
                  @click="on.group(header.value)"
                >
                  mdi-table-row-height
                </v-icon>
              </div>
            </th>
          </tr>
        </template>

        <template
          v-slot:[`group.header`]="{ group, headers, toggle, isOpen, remove }"
        >
          <td :colspan="headers.length + 1">
            <v-btn @click="toggle" x-small icon :ref="group">
              <v-icon v-if="!isOpen">mdi-plus</v-icon>
              <v-icon v-else>mdi-minus</v-icon>
            </v-btn>
            <span class="mx-5 font-weight-bold">{{ formatDate(group) }}</span>
            <v-btn @click="remove" x-small icon :ref="group">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </td>
        </template>

        <template v-slot:item="{ item: visit }">
          <tr
            @click="showRequestDetail(visit.request_id)"
            style="position: relative"
            :style="{
              cursor: isNavigating ? 'progress' : 'pointer',
              opacity: isNavigating ? 0.7 : 1,
            }"
          >
            <td v-show="showColumn('request_id')">
              <v-row
                class="d-flex align-content-center"
                style="flex-flow: nowrap; align-items: center"
              >
                <v-tooltip bottom>
                  <template
                    v-slot:activator="{ on, attrs }"
                    v-if="$store.state.app_display_scheme"
                  >
                    <div v-bind="attrs" v-on="on" class="mr-2">
                      <SchemeIcon
                        style="margin-top: 3px"
                        :scheme="visit.scheme_name"
                      />
                    </div>
                  </template>
                  <span>{{ visit.scheme_name }}</span>
                </v-tooltip>
                <span>{{ visit.request_id }}</span>
              </v-row>
            </td>
            <td
              v-show="showColumn('visit_type.id')"
              style="
                display: flex;
                align-items: center;
                justify-content: flex-start;
              "
              class="border-type"
            >
              <div
                :style="`margin-top: -2px; height: 12px; width: 12px; margin-right: 4px; border-radius: 50%; background-color: ${visit.visit_type?.backgroundColor}`"
              ></div>
              <span>{{ visit.visit_type.label }}</span>
            </td>
            <td v-show="showColumn('visit_status')">
              <div
                style="
                  display: flex;
                  align-items: center;
                  justify-content: flex-start;
                  gap: 8px;
                "
              >
                <v-icon color="warning" v-if="visit.visit_status === 1"
                  >event</v-icon
                >
                <v-icon color="success" v-if="visit.visit_status === 2"
                  >check</v-icon
                >
                <v-icon color="success" v-if="visit.visit_status === 14"
                  >check</v-icon
                >
                <v-icon
                  color="warning"
                  v-if="visit.visit_status === 3 || visit.visit_status === 4"
                  >error_outline</v-icon
                >
                <v-icon color="success" v-if="visit.visit_status === 8"
                  >home</v-icon
                >
                <v-icon color="success" v-if="visit.visit_status === 12"
                  >store</v-icon
                >
                <v-icon
                  color="error"
                  v-if="
                    visit.visit_status === 5 ||
                    visit.visit_status === 6 ||
                    visit.visit_status === 7 ||
                    visit.visit_status === 9 ||
                    visit.visit_status === 10 ||
                    visit.visit_status === 11 ||
                    visit.visit_status === 13
                  "
                >
                  clear
                </v-icon>
                <span>
                  {{ visit.visit_status_name }}
                </span>
              </div>
            </td>
            <td v-show="showClientColumn('client')">
              {{ visit?.client_fullname ?? "No disponible" }}
            </td>
            <td v-show="showColumn('service_name')">
              {{ visit?.service_name ?? "No disponible" }}
            </td>
            <td
              v-show="showColumn('request.full_address')"
              style="text-transform: capitalize"
            >
              {{ visit?.request_address ?? "No disponible" }}
            </td>
            <td
              v-show="showColumn('visit_date')"
              style="text-transform: capitalize"
            >
              {{ visit.visit_date }}
            </td>
            <td v-show="showColumn('arrival_time')">
              {{ visit.arrival_time }}
            </td>
            <td v-show="showColumn('departure_time')">
              {{ visit.departure_time }}
            </td>
          </tr>
        </template>
      </v-data-table>
    </v-row>
  </v-container>
</template>

<script>
import { debounce } from "lodash";
import moment from "moment";
import { mapActions, mapGetters, mapState } from "vuex";

import HeadersVisits from "../assets/js/headersVisits";
import Loader from "./Loader";
import SchemeIcon from "./SchemeIcon";
import VisitItem from "./VisitItem";

export default {
  name: "VisitList",

  components: {
    VisitItem,
    Loader,
    SchemeIcon,
  },

  data() {
    return {
      headersVisits: HeadersVisits,
      pagination: {
        page: 1,
        itemsPerPage: 10,
        rowsPerPageOptions: [5, 10, 20, 30],
        search: null,
        type: null,
      },
      isNavigating: false,
    };
  },

  created() {
    this.getAllVisits({ pagination: this.pagination });
  },

  mounted() {
    this.headersVisits = HeadersVisits;
    this.$store.commit("set_app_title", "Eventos");

    window.scrollTo(0, 0);

    this.setHeaders();
  },

  watch: {
    pagination: {
      handler: function (newPagination) {
        if (newPagination) {
          this.listVisits({ pagination: newPagination });
        }
      },
      deep: true,
    },
    "pagination.search": "resetPaginationPage",
    "pagination.type": "resetPaginationPage",

    "$store.state.headers": function (val, old) {
      localStorage.setItem(
        "headersVisits",
        JSON.stringify(this.$store.state.headers)
      );
    },
    "$store.state.updateHeaders": function () {
      this.setHeaders();
    },
  },
  methods: {
    ...mapActions("visit", ["getAllVisits"]),

    listVisits: debounce(async function ({ pagination }) {
      try {
        await this.getAllVisits({ pagination });
      } catch (error) {
        console.log(error);
      }
    }, 500),

    debounceSearch: debounce(function (event) {
      this.pagination.search = event;
    }, 500),

    debounceType: debounce(function (event) {
      this.pagination.type = event;
    }, 500),

    resetPaginationPage() {
      this.pagination.page = 1;
    },

    clearSearch() {
      this.pagination.search = null;
    },

    setHeaders() {
      const _headers = localStorage.getItem("headersVisits");
      let headers;
      if (_headers === JSON.stringify(this.headersVisits)) {
        headers = JSON.parse(_headers);
      } else {
        headers = this.headersVisits;
        localStorage.setItem("headersVisits", JSON.stringify(headers));
      }
      // this.injectFilters(headers);
      this.$store.commit("set_headers", headers);
    },

    // injectFilters(headers) {
    //   const filterType = (value) => {
    //     if (!this.$store.state.visit_type) return true;
    //     return value === this.$store.state.visit_type;
    //   };

    //   const filterStatus = (value) => {
    //     if (!this.$store.state.visit_status) return true;
    //     return value === this.$store.state.visit_status;
    //   };

    //   const index = headers.findIndex((item) => item.value === "visit_type.id");
    //   headers[index].filter = filterType;

    //   const indexStatus = headers.findIndex(
    //     (item) => item.value === "visit_status"
    //   );
    //   headers[indexStatus].filter = filterStatus;

    //   const indexB = this.headersVisits.findIndex(
    //     (item) => item.value === "visit_type.id"
    //   );
    //   this.headersVisits[indexB].filter = filterType;

    //   const indexStatusB = this.headersVisits.findIndex(
    //     (item) => item.value === "visit_status"
    //   );

    //   this.headersVisits[indexStatusB].filter = filterStatus;
    // },

    // getItemScheme(item) {
    //   const visitTechReq = this.$store.state.user_techReqs.find(
    //     (techReq) => techReq.id === item.request_technician
    //   );
    //   return visitTechReq.request.scheme;
    // },

    async showRequestDetail(requestId) {
      if (this.isNavigating) return;

      this.isNavigating = true;

      const navigationResponse = await this.$router.push({
        name: "request-detail",
        params: { id: requestId },
      });

      if (navigationResponse) {
        this.isNavigating = false;
      }
    },

    showColumn(value) {
      const item = this.$store.state.headers.find(function (element) {
        return element.value === value;
      });
      if (item) return item.show;
      return null;
    },

    showClientColumn(value) {
      const item = this.$store.state.headers.find(function (element) {
        return element.value === value;
      });
      if (item && this.$store.state.app_display_username) return item.show;
      return null;
    },

    formatDate(date) {
      const _date = new Date();
      const today = moment(_date).format("YYYY-MM-DD");
      if (date === today) {
        return "Hoy";
      } else {
        return moment(date, "YYYY-MM-DD").format("dddd DD MMMM, YYYY");
      }
    },
  },
  computed: {
    ...mapState("visit", ["visits", "totalVisits", "typeOptions"]),
    ...mapGetters("visit", ["visitLoading"]),

    computedHeaders: function () {
      let _headers = this.headersVisits;
      if (!this.$store.state.app_display_username) {
        _headers = _headers.filter((item) => item.value !== "client");
      }
      return _headers;
    },
  },
};
</script>

<style scoped>
img {
  width: 25px;
}

.border-type {
  border-bottom-color: transparent !important;
}

@media (max-width: 959px) {
  .v-divider {
    margin-top: 10px;
    margin-bottom: 10px;
  }

  .hidden-sm {
    display: none;
  }

  .section-container {
    padding: 0 !important;
    background: #eeeeee;
  }

  .section-content {
    display: flex;
    flex-direction: column-reverse;
    overflow-y: scroll;
  }

  .date {
    text-align: center;
  }
}

@media (min-width: 960px) {
  .v-divider {
    margin-top: 15px;
    margin-bottom: 15px;
  }

  .hidden-lg {
    display: none;
  }

  .date {
    text-align: left;
  }
}

.date {
  text-transform: capitalize;
  background: #eeeeee;
  padding-top: 24px;
}

.dateRow {
  background: #eeeeee;
  text-transform: capitalize;
  font-weight: bold;
}

.today {
  background: #e1f5fe;
}

.v-list-item__subtitle,
.v-list-item__title {
  overflow: visible !important;
}

.v-data-footer {
  padding: 0 !important;
}

.v-application--is-ltr .v-data-footer__select {
  margin-right: 0 !important;
}

.loader-container {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
